import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./NgoBooking.css";

const API_BASE = "https://backend-xhl4.onrender.com";

export default function NGOBooking() {
  const [step, setStep] = useState(0); // start at Step 0 (NGO list)
  const [ngos, setNgos] = useState([]);
  const [selectedNgo, setSelectedNgo] = useState(null);

  // step 1 fields (date/time)
  const [date, setDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("morning");

  // step 2 OTP
  const [phone, setPhone] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otp, setOtp] = useState("");

  // step 3 patient details
  const [patient, setPatient] = useState({
    Name: "",
    Age: "",
    Gender: "",
    Location: "",
    Problem: [],
  });
  const [problemText, setProblemText] = useState(""); // to add one problem
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchNgos();
  }, []);

  async function fetchNgos() {
    try {
      const res = await fetch(`${API_BASE}/AppointmentRoute/ngo/sponsored`);
      const data = await res.json();
      setNgos(data.ngos || []);
    } catch (err) {
      console.error(err);
    }
  }

  // Step 1: check availability
  async function checkAvailability() {
    if (!selectedNgo) return setError("Select an NGO first.");
    if (!date) return setError("Select a date.");
    setError("");
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}/AppointmentRoute/ngo/check-availability`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ngoCode: selectedNgo.companyCode,
            date,
            preferredTime,
          }),
        }
      );
      const data = await res.json();
      setLoading(false);
      if (res.ok && data.available) {
        setStep(2); // move to OTP step
      } else {
        setError(data.message || "No slots available for selected date/time.");
      }
    } catch (err) {
      setLoading(false);
      setError("Network error while checking availability.");
    }
  }

  // Step 2: OTP send
  async function sendOtp() {
    if (!phone || phone.length < 10)
      return setError("Enter a 10-digit mobile number.");
    setError("");
    setIsSendingOtp(true);
    try {
      const r = await fetch(`${API_BASE}/OtpRoute/send-otp/${phone}`);
      const data = await r.json();
      setIsSendingOtp(false);
      if (r.ok) {
        // show OTP field in same step
        setStep(2.5);
      } else {
        setError(data.message || "Failed to send OTP.");
      }
    } catch (err) {
      setIsSendingOtp(false);
      setError("Network error sending OTP.");
    }
  }

  // verify OTP
  async function verifyOtp() {
    if (!otp) return setError("Enter OTP.");
    setError("");
    setIsVerifyingOtp(true);
    try {
      const r = await fetch(`${API_BASE}/OtpRoute/verify-otp/${phone}/${otp}`);
      const data = await r.json();
      setIsVerifyingOtp(false);
      if (r.ok) {
        // move to patient details
        setStep(3);
      } else {
        setError(data.message || "OTP verification failed.");
      }
    } catch (err) {
      setIsVerifyingOtp(false);
      setError("Network error verifying OTP.");
    }
  }

  // Step 3: patient details validation + create appointment (Step 4)
  async function bookNgo() {
    // Validate required
    if (
      !patient.Name ||
      !patient.Age ||
      !patient.Gender ||
      !patient.Location ||
      patient.Problem.length === 0
    ) {
      return setError(
        "Please fill all required patient details and at least one problem."
      );
    }
    setError("");
    setLoading(true);
    try {
      // Call booking endpoint
      const res = await fetch(`${API_BASE}/AppointmentRoute/ngo/auto-book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ngoCompanyCode: selectedNgo.companyCode,
          ngoName: selectedNgo.companyName,
          date,
          preferredTime,
          patient: {
            Name: patient.Name,
            Mobile: phone,
            Age: patient.Age,
            Gender: patient.Gender,
            Location: patient.Location,
            Problem: patient.Problem,
          },
        }),
      });
      const data = await res.json();
      setLoading(false);
      if (res.ok) {
        setConfirmData(data);
        setStep(4);
      } else {
        setError(data.message || "Booking failed.");
      }
    } catch (err) {
      setLoading(false);
      setError("Network error while booking.");
    }
  }

  // UI blocks for each step
  if (step === 0) {
    // Step 0: list NGOs
    return (
      <div className="ngo-page">
        <div className="ngo-center-card">
          <h2>NGO Sponsored Slots</h2>
          {ngos.length === 0 ? (
            <div className="no-ngo">
              <h3>No NGO sponsored slots available right now</h3>
              <p>
                We’re working to bring more sponsored slots. Please check again
                later.
              </p>
            </div>
          ) : (
            <>
              <p>
                If you believe you need financial support, you may continue with
                an NGO-sponsored slot.
              </p>
              <div className="ngo-list">
                {ngos.map((n) => (
                  <div key={n.companyCode} className="ngo-card-small">
                    <div>
                      <strong>{n.companyName}</strong>
                      {/* <div className="ngo-meta">Credits: {n.totalCredits}</div> */}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedNgo(n);
                        setStep(1);
                      }}
                    >
                      Use this NGO
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (step === 1) {
    // Step 1: choose date + preferredTime
    return (
      <div className="ngo-page">
        <div className="ngo-center-card">
          <h2>Choose Date & Preferred Slot</h2>
          <div className="selected-ngo">
            Using NGO: <strong>{selectedNgo.companyName}</strong>
          </div>
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <label>Preferred Slot</label>
          <select
            value={preferredTime}
            onChange={(e) => setPreferredTime(e.target.value)}
          >
            <option value="morning">Morning</option>
            <option value="afternoon">Afternoon</option>
            <option value="evening">Evening</option>
          </select>
          {error && <div className="error">{error}</div>}
          <div className="two-btns">
            <button onClick={checkAvailability} disabled={loading}>
              {loading ? "Checking..." : "Check Availability"}
            </button>
            <button
              className="ghost"
              onClick={() => {
                setSelectedNgo(null);
                setStep(0);
              }}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 2 || step === 2.5) {
    // Step 2: phone -> send otp -> verify
    return (
      <div className="ngo-page">
        <div className="ngo-center-card">
          <h2>Verify Phone</h2>
          <div className="selected-ngo">
            Using NGO: <strong>{selectedNgo.companyName}</strong>
          </div>
          <label>Phone</label>
          <input
            placeholder="10-digit mobile"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {step === 2 && (
            <button onClick={sendOtp} disabled={isSendingOtp}>
              {isSendingOtp ? "Sending..." : `Send OTP to ${phone || "mobile"}`}
            </button>
          )}
          {step === 2.5 && (
            <>
              <label>Enter OTP</label>
              <input
                placeholder="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              <div className="two-btns">
                <button onClick={verifyOtp} disabled={isVerifyingOtp}>
                  {isVerifyingOtp ? "Verifying..." : "Verify OTP"}
                </button>
                <button className="ghost" onClick={() => setStep(1)}>
                  Back
                </button>
              </div>
            </>
          )}
          {error && <div className="error">{error}</div>}
        </div>
      </div>
    );
  }

  if (step === 3) {
    // Step 3: patient details
    return (
      <div className="ngo-page">
        <div className="ngo-center-card">
          <h2>Patient Details</h2>
          <div className="selected-ngo">
            Using NGO: <strong>{selectedNgo.companyName}</strong>
          </div>

          <input
            placeholder="Full name"
            value={patient.Name}
            onChange={(e) =>
              setPatient((p) => ({ ...p, Name: e.target.value }))
            }
          />
          <input
            placeholder="Age"
            type="number"
            value={patient.Age}
            onChange={(e) => setPatient((p) => ({ ...p, Age: e.target.value }))}
          />
          <select
            value={patient.Gender}
            onChange={(e) =>
              setPatient((p) => ({ ...p, Gender: e.target.value }))
            }
            className="ngo-input"
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>

          <input
            placeholder="Location"
            value={patient.Location}
            onChange={(e) =>
              setPatient((p) => ({ ...p, Location: e.target.value }))
            }
          />

          <div className="problem-input">
            <input
              placeholder="Add problem (press Add)"
              value={problemText}
              onChange={(e) => setProblemText(e.target.value)}
            />
            <button
              className="small"
              onClick={() => {
                if (!problemText) return;
                setPatient((p) => ({
                  ...p,
                  Problem: [...(p.Problem || []), problemText],
                }));
                setProblemText("");
              }}
            >
              Add
            </button>
          </div>

          {patient.Problem && patient.Problem.length > 0 && (
            <div className="problem-list">
              {patient.Problem.map((pr, idx) => (
                <div key={idx} className="pill">
                  {pr}{" "}
                  <span
                    onClick={() =>
                      setPatient((p) => ({
                        ...p,
                        Problem: p.Problem.filter((_, i) => i !== idx),
                      }))
                    }
                  >
                    ✕
                  </span>
                </div>
              ))}
            </div>
          )}

          {error && <div className="error">{error}</div>}

          <div className="two-btns">
            <button onClick={bookNgo} disabled={loading}>
              {loading ? "Booking..." : "Book & Confirm"}
            </button>
            <button className="ghost" onClick={() => setStep(2)}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 4 && confirmData) {
    // Step 4: confirmation
    return (
      <div className="ngo-page">
        <div className="ngo-center-card success">
          <h2>Booking Confirmed 🎉</h2>
          {/* <p>
            <strong>Appointment ID:</strong> {confirmData.appointmentId}
          </p> */}
          <p>
            <strong>Date:</strong>{" "}
            {new Date(
              confirmData.appointment.DateOfAppointment
            ).toLocaleDateString()}
          </p>
          <p>
            <strong>Time:</strong> {confirmData.appointment.AppStartTime}
          </p>
          {/* <p>
            <strong>NGO Remaining Credits:</strong>{" "}
            {confirmData.ngoRemainingCredits}
          </p> */}
          <div className="two-btns">
            <button onClick={() => navigate("/")}>Go Home</button>
            {/* <button
              className="ghost"
              onClick={() => {
                setStep(0);
                setConfirmData(null);
              }}
            >
              Book another
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
